import { FastField, Field } from 'formik';
import React, { useState } from 'react';
import { BrowserView } from 'react-device-detect';
import { Link } from 'react-router-dom';
import { date, object } from 'yup';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Form, { Assinatura, CaixaDeTexto, Campo } from 'Component/Form';
import Imagem from 'Component/Imagem';
import Modal from 'Component/Modal';
import AvaliacaoDesempenho from 'Interface/AvaliacoesDesempenho';
import VinculoEmpregaticio from 'Interface/VinculosEmpregaticios';
import {
  useAdicionarAvaliacaoDesempenhoMutation,
  useEditarAvaliacaoDesempenhoMutation,
  useRecuperarAvaliacoesDesempenhoQuery,
} from 'View/Service/Default/AvaliacoesDesempenho';

interface Props {
  dados: VinculoEmpregaticio;
}

const regras = object({
  proxima_avaliacao: date().required('Obrigatório'),
});

const Lista = ({ dados }: Props): React.ReactElement => {
  const toast = useToast();
  const [modal, setModal] = useState<boolean>(false);
  const [tipo, setTipo] = useState<number>(1);
  const [adicionarAvaliacao] = useAdicionarAvaliacaoDesempenhoMutation();
  const [editarAvaliacao] = useEditarAvaliacaoDesempenhoMutation();

  const {
    data: { avaliacoes: [avaliacao] } = {
      avaliacoes: [{} as AvaliacaoDesempenho],
    },
  } = useRecuperarAvaliacoesDesempenhoQuery({
    options: { usuario_id: dados.usuario_id },
  });

  const enviar = async (dadosForm: AvaliacaoDesempenho): Promise<void> => {
    try {
      if (typeof dadosForm.assinatura_funcionario === 'string') {
        delete dadosForm.assinatura_funcionario;
      }
      if (typeof dadosForm.assinatura_gestor === 'string') {
        delete dadosForm.assinatura_gestor;
      }

      if (avaliacao && avaliacao.id) {
        await editarAvaliacao(dadosForm).unwrap();

        toast({ conteudo: 'Editado com sucesso!', tema: 'success' });
      } else {
        await adicionarAvaliacao({
          ...dadosForm,
          vinculo_empregaticio_id: dados.id,
        }).unwrap();

        toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });
      }

      setModal(false);
    } catch (error) {
      console.log(error);
      toast({
        titulo: 'Erro ao adicionar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <React.Fragment>
      <div className="d-flex border-bottom align-items-center py-2 px-3">
        <Link to={`/usuarios/editar/${dados.usuario_id}`}>
          <Imagem
            src={dados.usuario?.imagem}
            alt={dados.usuario?.nome}
            className="rounded-circle object-fit-cover"
            width="80"
            height="80"
          />
        </Link>
        <div className="ms-4 fs-4">
          <Link
            to={`/usuarios/editar/${dados.usuario_id}`}
            className="text-decoration-none"
          >
            {dados.usuario?.nome}
          </Link>
          <small className="d-block fs-6 text-muted">
            {dados.empresa?.nome_fantasia}
          </small>
        </div>
        <BrowserView className="d-flex flex-grow-1 align-items-center justify-content-end">
          <div className="ms-5 fs-5 text-end">
            <div className="fs-4">
              {dados.admissao.data().toLocaleString()}
              <small className="d-block fs-6 text-muted">Admissão em</small>
            </div>
          </div>
          <div className="ms-2 fs-5 text-end">
            <Botao
              icone="cloud-upload-fill"
              tema="success"
              className="ms-5"
              onClick={() => setModal(true)}
            />
          </div>
        </BrowserView>
      </div>
      <Modal telaCheia exibir={modal} titulo="Avaliação" aoFechar={setModal}>
        <div className="d-flex justify-content-center gap-3 mb-3">
          <Botao
            tema={tipo == 1 ? 'primary' : 'outline-primary'}
            onClick={() => setTipo(1)}
          >
            Avaliação para não gestores
          </Botao>
          <Botao
            tema={tipo == 2 ? 'primary' : 'outline-primary'}
            onClick={() => setTipo(2)}
          >
            Avaliação para gestores
          </Botao>
        </div>
        <Form onSubmit={enviar} inicial={avaliacao} regras={regras}>
          <>
            {tipo == 1 && (
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <td colSpan={2}>INDICADORES DE COMPETÊNCIA</td>
                    <td colSpan={4}>AUTO AVALIAÇÃO COLABORADOR</td>
                    <td colSpan={4}>AVALIAÇÃO DO GESTOR</td>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={2} className="fw-bold">
                      Preparo e Qualificação:
                    </td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                  </tr>
                  <tr>
                    <td colSpan={2} width="70%">
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.0.texto"
                        value="Conhecimento, habilidades e experiência necessários às suas atividades."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.1.texto"
                        value="Organização"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.2.texto"
                        value="Comunicação"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.3.texto"
                        value="Sugere e implanta novas práticas de trabalho visando a melhoria das atividades e processos."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="fw-bold">
                      Capacidade de trabalho em equipe:
                    </td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.4.texto"
                        value="Cooperação no compartilhamento de ideias, atividades e soluções com as demais áreas."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.5.texto"
                        value="Demonstra capacidade de motivar seus colegas e colaboradores."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="fw-bold">
                      Compromisso com resultados:
                    </td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.6.texto"
                        value="Concretiza com eficácia e eficiência os objetivos de seu trabalho."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.7.texto"
                        value="Busca orientação para solucionar problemas/dúvidas do dia-a-dia diante de situações imprevistas."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.8.texto"
                        value="Esforça-se sempre para superar os resultados estabelecidos previamente, levando em consideração recursos, prazos, custos e qualidade."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="fw-bold">
                      Visão institucional:
                    </td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.9.texto"
                        value="Procura conhecer a estrutura e funcionamento da empresa e os principais produtos e serviços por ela oferecidos. Conhece os impactos de seu trabalho nas atividades dos outros colegas/áreas."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.10.texto"
                        value="Compromisso com valores da empresa"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="fw-bold">
                      Características comportamentais:
                    </td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.11.texto"
                        value="Realiza seu trabalho de forma equilibrada e convive bem com pressões, imprevistos,  e conflitos. Recebe bem críticas e as trata adequadamente, admitindo erros sem 'caçar culpados', apresentando baixo índice de desculpas ou pretextos."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.12.texto"
                        value="Trata a todos com respeito, simpatia e educação, independentemente da hierarquia, buscando um clima de harmonia, confiança e cooperação. Demonstra paciência e capacidade de conviver com diferenças (ritmo, estilo, pensamentos, etc)."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.13.texto"
                        value="Antecipa-se, tirando proveito de oportunidades e não deixando acontecer falhas que possam ser evitadas. Demonstra iniciativa diante de imprevistos e pressões."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.14.texto"
                        value="Consegue colocar-se no lugar do outro para compreendê-lo sob o ponto de vista dele."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.15.texto"
                        value="Apresenta-se em seu local de trabalho no horário. Cumpre as normas da entidade e os compromissos de trabalho (reuniões, treinamentos, etc).  Zela pelos bens da entidade."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </>
          <>
            {tipo == 2 && (
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <td colSpan={2}>INDICADORES DE COMPETÊNCIA</td>
                    <td colSpan={4}>AUTO AVALIAÇÃO COLABORADOR</td>
                    <td colSpan={4}>AVALIAÇÃO DO GESTOR</td>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={2} className="fw-bold">
                      Preparo e Qualificação:
                    </td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                  </tr>
                  <tr>
                    <td colSpan={2} width="70%">
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.0.texto"
                        value="Conhece as atividades e processos da área assegurando corretas orientações para sua equipe. Acompanha os trabalhaos, intervindo quando necessário, explicando detalhes e orientando quando solicitado."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.0.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.1.texto"
                        value="Comunicação"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.1.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.2.texto"
                        value="Negocia com habilidade com fornecedores, colaboradores, clientes, e prestadores de serviços, observando regras, acordos e contratos."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.2.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.3.texto"
                        value="Busca, sistematiza, registra e dissemina o conhecimento, de modo a transformá-lo em vantagem para a entidade."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.3.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="fw-bold">
                      Capacidade de gestão:
                    </td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.4.texto"
                        value="Mobiliza, envolve e compromete seus colaboradores visando a realização dos objetivos e metas definidas e estimulando a equipe a valorizar o que faz."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.4.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.5.texto"
                        value="Delega e compartilha responsabilidades, tarefas e decisões. Exerce o feedback, praticando o elogio e a crítica, reconhecendo e compartilhando resultados com a equipe."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.5.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.6.texto"
                        value="Auxilia os colaboradores no desenvolvimento dos seus potenciais, gera um clima organizacional favorável ao desenvolvimento dos profissionais e forma sucessores."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.6.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.7.texto"
                        value="Trata conflitos, ouvindo, avaliando e decidindo com imparcialidade, dando espaço para a equipe discordar e influenciar em decisões e processos."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.7.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="fw-bold">
                      Capacidade de trabalho em equipe:
                    </td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.8.texto"
                        value="Cooperação no compartilhamento de ideias, atividades e soluções com as demais áreas."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.8.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.9.texto"
                        value="Demonstra capacidade de motivar seus colegas e colaboradores."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.9.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="fw-bold">
                      Compromisso com resultados:
                    </td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.10.texto"
                        value="Concretiza com eficácia e eficiência os objetivos de seu trabalho."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.10.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.11.texto"
                        value="Estabelece prioridades, organiza as atividades e as executa adequadamente dentro do prazo para assegurar os objetivos da empresa."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.11.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.12.texto"
                        value="Busca orientação para solucionar problemas/dúvidas do dia-a-dia diante de situações imprevistas."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.12.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.13.texto"
                        value="Esforça-se sempre para superar os resultados estabelecidos previamente, levando em consideração recursos, prazos, custos e qualidade."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.13.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="fw-bold">
                      Visão institucional:
                    </td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.14.texto"
                        value="Procura conhecer a estrutura e funcionamento da empresa e os principais produtos e serviços por ela oferecidos. Conhece os impactos de seu trabalho nas atividades dos outros colegas/áreas."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.14.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.15.texto"
                        value="Compromisso com valores da empresa"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.15.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.16.texto"
                        value="Identifica e compreende as necessidades dos clientes e suas expectativas com relação à qualidade dos produtos e serviços da empresa."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.16.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.16.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.16.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.16.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.16.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.16.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.16.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.16.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.17.texto"
                        value="Demonstra conhecimento das normas e procedimentos da entidade e compreende a importância de seu cumprimento e atua nesse sentido."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.17.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.17.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.17.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.17.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.17.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.17.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.17.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.17.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="fw-bold">
                      Características comportamentais:
                    </td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                    <td className="fw-bold">RUIM</td>
                    <td className="fw-bold">REGULAR</td>
                    <td className="fw-bold">BOM</td>
                    <td className="fw-bold">ÓTIMO</td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.18.texto"
                        value="Realiza seu trabalho de forma equilibrada e convive bem com pressões, imprevistos,  e conflitos. Recebe bem críticas e as trata adequadamente, admitindo erros sem 'caçar culpados', apresentando baixo índice de desculpas ou pretextos."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.18.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.18.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.18.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.18.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.18.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.18.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.18.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.18.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.19.texto"
                        value="Trata a todos com respeito, simpatia e educação, independentemente da hierarquia, buscando um clima de harmonia, confiança e cooperação. Demonstra paciência e capacidade de conviver com diferenças (ritmo, estilo, pensamentos, etc)."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.19.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.19.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.19.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.19.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.19.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.19.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.19.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.19.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.20.texto"
                        value="Antecipa-se, tirando proveito de oportunidades e não deixando acontecer falhas que possam ser evitadas. Demonstra iniciativa diante de imprevistos e pressões."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.20.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.20.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.20.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.20.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.20.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.20.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.20.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.20.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <Field
                        component="textarea"
                        style={{
                          width: '100%',
                          height: 'auto',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        readonly
                        name="dados.21.texto"
                        value="Consegue colocar-se no lugar do outro para compreendê-lo sob o ponto de vista dele."
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.21.valor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.21.valor"
                        value="Regular"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.21.valor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.21.valor"
                        value="Ótimo"
                      />
                    </td>
                    <td className="border-start">
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.21.valor_gestor"
                        value="Ruim"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.21.valor_gestor"
                        value="Regular"
                        semMargem
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.21.valor_gestor"
                        value="Bom"
                      />
                    </td>
                    <td>
                      <FastField
                        type="radio"
                        style={{ width: 30, height: 30 }}
                        name="dados.21.valor_gestor"
                        value="Ótimo"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </>

          <CaixaDeTexto name="observacoes" rotulo="Observações" />

          <div className="row">
            <div className="col-md-6">
              <Assinatura
                name="assinatura_funcionario"
                rotulo="Assinatura do funcionário"
              />
            </div>
            <div className="col-md-6">
              <Assinatura
                name="assinatura_gestor"
                rotulo="Assinatura do gestor"
              />
            </div>
          </div>

          <Campo
            name="proxima_avaliacao"
            type="date"
            rotulo="Próxima avaliação"
          />

          <div>
            <Botao type="submit">Enviar</Botao>{' '}
            <Botao tema="light" onClick={() => setModal(false)}>
              Cancelar
            </Botao>
          </div>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default Lista;
